import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, Paper, TextField, Button, Box, Grid, Alert
} from '@mui/material';
import api from '../utils/api';

const SellerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    // If in edit mode, fetch the seller data
    if (isEditMode) {
      fetchSellerData();
    }
  }, [isEditMode, id]);
  
  const fetchSellerData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/sellers/${id}`);
      setFormData(response.data);
    } catch (err) {
      console.error('Error fetching seller data:', err);
      setError('Virhe myyjän tietojen haussa. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (isEditMode) {
        await api.put(`/api/sellers/${id}`, formData);
        setSuccess('Myyjä päivitetty onnistuneesti!');
      } else {
        await api.post('/api/sellers', formData);
        setSuccess('Uusi myyjä luotu onnistuneesti!');
        // Reset form after successful creation
        setFormData({
          name: '',
          email: '',
          phone: ''
        });
      }
      
      // Navigate back to list after a short delay
      setTimeout(() => {
        navigate('/sellers');
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Virhe lomakkeen lähetyksessä. Tarkista tiedot ja yritä uudelleen.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/sellers');
  };

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEditMode ? 'Muokkaa myyjää' : 'Uusi myyjä'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
      )}
      
      <Paper elevation={3} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nimi"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sähköposti"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Puhelin"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Peruuta
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? 'Tallennetaan...' : (isEditMode ? 'Päivitä' : 'Tallenna')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
};

export default SellerForm;
